import * as opt from "lib/opt"
import { createTheme as createMuiTheme } from "@mui/material"
import { ThemeProvider as MuiThemeProvider, Theme as MuiThemeConfig } from "@mui/material/styles"
import {
	ConfigProvider as AntdConfigProvider,
	theme as antdRawTheme,
	type ThemeConfig as AntdThemeConfig,
} from "antd"
import { createContext, useCallback, useContext, useMemo } from "react"
import * as hooks from "lib/hooks"
import { alpha } from "@mui/material/styles"

export interface ColorTheme {
	border: string
	borderHover: string
	borderFocus: string
	background: string
	calendarBackground: string
	shadow: string
	text: string
	secondaryText: string
	activeText: string
	disabledText: string
	error: string
	ripple: string
}

export interface MaterialColors {
	primary: string
	primaryHover?: string
	onPrimary?: string
	surfaceContainer: string
	surfaceContainerHigh: string
	onSurface: string
	onSurfaceVariant: string
	outline: string
	error: string
	isDark: boolean
	surfaceDark?: {
		onSurface: string
	}
	surfaceLight?: {
		onSurface: string
	}
	elevation?: {
		elevation3: string
	}
	shape?: {
		corner: {
			small: number
		}
	}
}

// Material You color palette (M3 Design System)
// Official Google Material Design 3 color palette - UPDATED TO BLUE
const materialColors = {
	// Primary colors - Official Material You blue
	primary: {
		0: "#000000",
		10: "#001D35", // Darkest blue
		20: "#00315B",
		30: "#004881",
		40: "#0061A4", // Primary blue for light mode
		50: "#007ACE",
		60: "#0094F7",
		70: "#4FB3FF",
		80: "#A1C9FF", // Primary blue for dark mode
		90: "#D3E4FF",
		95: "#ECF3FF",
		99: "#FBFCFF",
		100: "#FFFFFF",
	},
	// Secondary colors - Official Material You secondary blue tones
	secondary: {
		0: "#000000",
		10: "#001D32",
		20: "#003355",
		30: "#004A77",
		40: "#00639B", // Secondary light mode
		50: "#007CC1",
		60: "#0097E9",
		70: "#3CB1FF",
		80: "#9CCAFF", // Secondary dark mode
		90: "#CFE5FF",
		95: "#E8F2FF",
		99: "#F8FBFF",
		100: "#FFFFFF",
	},
	// Tertiary colors - Official Material You blue-green tertiary
	tertiary: {
		0: "#000000",
		10: "#002022",
		20: "#00363B",
		30: "#004F55",
		40: "#006970", // Tertiary light mode
		50: "#00838D",
		60: "#009EA9",
		70: "#00BAC6",
		80: "#43D5E3", // Tertiary dark mode
		90: "#A9EFFA",
		95: "#D5F7FC",
		99: "#F2FBFD",
		100: "#FFFFFF",
	},
	// Error colors - Official Material You error colors (unchanged)
	error: {
		0: "#000000",
		10: "#410E0B",
		20: "#601410",
		30: "#8C1D18",
		40: "#B3261E", // Error light mode
		50: "#DC362E",
		60: "#E46962",
		70: "#EC928E",
		80: "#F2B8B5", // Error dark mode
		90: "#F9DEDC",
		95: "#FCEEEE",
		99: "#FFFBF9",
		100: "#FFFFFF",
	},
	// Neutral colors - Official Material You neutral colors with blue tint
	neutral: {
		0: "#000000",
		10: "#1A1C1E", // Slightly blue-tinted neutrals
		20: "#2E3133",
		30: "#45494D",
		40: "#5D6166",
		50: "#767A7F",
		60: "#909498",
		70: "#AAAFB3",
		80: "#C5CACF",
		90: "#E1E4E8",
		95: "#EFF1F5",
		99: "#FBFCFF",
		100: "#FFFFFF",
	},
	// Neutral variant colors - Official Material You neutral variant with blue tint
	neutralVariant: {
		0: "#000000",
		10: "#151C22", // Blue-tinted neutral variants
		20: "#2A3139",
		30: "#404752", // Used for medium emphasis text in light mode
		40: "#575E68",
		50: "#70777F", // Used for outlines in light mode
		60: "#899198", // Used for outlines in dark mode
		70: "#A4ACB5",
		80: "#BFC7CF", // Used for medium emphasis text in dark mode
		90: "#DBE2EA",
		95: "#EAF0F9",
		99: "#F8FAFF",
		100: "#FFFFFF",
	},
	// Dark mode surfaces - Official Material You dark theme with blue tint
	surfaceDark: {
		0: "#1A1C1E", // Default background
		1: "#22262B", // Surface with 5% primary overlay
		2: "#2A2E33", // Surface with 8% primary overlay
		3: "#31353B", // Surface with 11% primary overlay
		4: "#383C43", // Surface with 14% primary overlay
		5: "#404550", // Surface with 17% primary overlay - high elevation
		variant: "#404752", // Surface variant - different undertone for containers
		dim: "#131618", // Dimmed version for modals/dialogs
		bright: "#4A4F58", // Brighter for cards that need to pop
		containerLow: "#2A2E33", // Low emphasis containers
		containerHigh: "#383C43", // High emphasis containers
		onSurface: "#E1E4E8", // Text on surface
		onSurfaceVariant: "#BFC7CF", // Lower emphasis text
	},
	// Light mode surfaces - Official Material You light theme with blue tint
	surfaceLight: {
		0: "#FBFCFF", // Default background
		1: "#F4F7FB", // Surface with 5% primary overlay
		2: "#F1F4F8", // Surface with 8% primary overlay
		3: "#EEF1F5", // Surface with 11% primary overlay
		4: "#EBF0F4", // Surface with 14% primary overlay
		5: "#E8EDF2", // Surface with 17% primary overlay - high elevation
		variant: "#DBE2EA", // Surface variant - different undertone for containers
		dim: "#DCDFE3", // Dimmed version for subtle backgrounds
		bright: "#FFFFFF", // Bright white for cards that need to pop
		containerLow: "#F1F4F8", // Low emphasis containers
		containerHigh: "#D3E4FF", // High emphasis containers
		onSurface: "#1A1C1E", // Text on surface
		onSurfaceVariant: "#404752", // Lower emphasis text
	},
}

// Material You elevation tokens for shadows
const elevationTokens = {
	light: {
		elevation0: "none", // No elevation
		elevation1: "0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px rgba(0, 0, 0, 0.3)", // Cards, small dialogs
		elevation2: "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px rgba(0, 0, 0, 0.3)", // Bottom navigation, search bars
		elevation3: "0px 4px 8px 3px rgba(0, 0, 0, 0.15), 0px 1px 3px rgba(0, 0, 0, 0.3)", // Navigation drawer, side sheets
		elevation4: "0px 6px 10px 4px rgba(0, 0, 0, 0.15), 0px 2px 3px rgba(0, 0, 0, 0.3)", // Floating action buttons (FABs)
		elevation5: "0px 8px 12px 6px rgba(0, 0, 0, 0.15), 0px 4px 4px rgba(0, 0, 0, 0.3)", // Modal bottom sheets, dialogs
	},
	dark: {
		elevation0: "none", // No elevation
		elevation1: "0px 1px 3px 1px rgba(0, 0, 0, 0.25), 0px 1px 2px rgba(0, 0, 0, 0.4)", // Slightly stronger for dark mode
		elevation2: "0px 2px 6px 2px rgba(0, 0, 0, 0.25), 0px 1px 2px rgba(0, 0, 0, 0.4)",
		elevation3: "0px 4px 8px 3px rgba(0, 0, 0, 0.25), 0px 1px 3px rgba(0, 0, 0, 0.4)",
		elevation4: "0px 6px 10px 4px rgba(0, 0, 0, 0.25), 0px 2px 3px rgba(0, 0, 0, 0.4)",
		elevation5: "0px 8px 12px 6px rgba(0, 0, 0, 0.25), 0px 4px 4px rgba(0, 0, 0, 0.4)",
	},
}

// Material You shape scale
const shapeTokens = {
	corner: {
		extraSmall: 4,
		small: 8,
		medium: 12,
		large: 16,
		extraLarge: 28,
		full: 999,
	},
}

// Material Design 3 typography scale
const typographyTokens = {
	display: {
		large: {
			fontSize: 57,
			lineHeight: 1.12,
			fontWeight: 400,
			letterSpacing: -0.25,
		},
		medium: {
			fontSize: 45,
			lineHeight: 1.16,
			fontWeight: 400,
			letterSpacing: 0,
		},
		small: {
			fontSize: 36,
			lineHeight: 1.22,
			fontWeight: 400,
			letterSpacing: 0,
		},
	},
	headline: {
		large: {
			fontSize: 32,
			lineHeight: 1.25,
			fontWeight: 400,
			letterSpacing: 0,
		},
		medium: {
			fontSize: 28,
			lineHeight: 1.29,
			fontWeight: 400,
			letterSpacing: 0,
		},
		small: {
			fontSize: 24,
			lineHeight: 1.33,
			fontWeight: 400,
			letterSpacing: 0,
		},
	},
	title: {
		large: {
			fontSize: 22,
			lineHeight: 1.27,
			fontWeight: 400,
			letterSpacing: 0,
		},
		medium: {
			fontSize: 16,
			lineHeight: 1.5,
			fontWeight: 500,
			letterSpacing: 0.15,
		},
		small: {
			fontSize: 14,
			lineHeight: 1.43,
			fontWeight: 500,
			letterSpacing: 0.1,
		},
	},
	body: {
		large: {
			fontSize: 16,
			lineHeight: 1.5,
			fontWeight: 400,
			letterSpacing: 0.5,
		},
		medium: {
			fontSize: 14,
			lineHeight: 1.43,
			fontWeight: 400,
			letterSpacing: 0.25,
		},
		small: {
			fontSize: 12,
			lineHeight: 1.33,
			fontWeight: 400,
			letterSpacing: 0.4,
		},
	},
	label: {
		large: {
			fontSize: 14,
			lineHeight: 1.43,
			fontWeight: 500,
			letterSpacing: 0.1,
		},
		medium: {
			fontSize: 12,
			lineHeight: 1.33,
			fontWeight: 500,
			letterSpacing: 0.5,
		},
		small: {
			fontSize: 11,
			lineHeight: 1.45,
			fontWeight: 500,
			letterSpacing: 0.5,
		},
	},
}

// Theme context type
export interface ThemeCtxType {
	toggleTheme: () => void
	theme: MuiThemeConfig
	antdTheme: AntdThemeConfig
	mode: "light" | "dark"
	colors: typeof materialColors
	elevation: typeof elevationTokens
	shape: typeof shapeTokens
	typography: typeof typographyTokens
}

const key = "theme-setting"
const themeCtx = createContext<opt.Opt<ThemeCtxType>>(opt.None())

export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const [mode, setMode] = hooks.UseLocalStorageState<"light" | "dark">(key, "light")

	// MUI Theme with accurate Material You tokens
	const muiTheme = useMemo(
		() =>
			createMuiTheme({
				palette: {
					mode,
					primary: {
						main: materialColors.primary[40],
						light: materialColors.primary[60],
						dark: materialColors.primary[30],
						contrastText:
							mode === "dark"
								? materialColors.primary[20]
								: materialColors.primary[100],
					},
					secondary: {
						main: materialColors.secondary[40],
						light: materialColors.secondary[60],
						dark: materialColors.secondary[30],
						contrastText:
							mode === "dark"
								? materialColors.secondary[20]
								: materialColors.secondary[100],
					},
					error: {
						main: materialColors.error[40],
						light: materialColors.error[60],
						dark: materialColors.error[30],
						contrastText:
							mode === "dark" ? materialColors.error[20] : materialColors.error[100],
					},
					background: {
						default:
							mode === "dark"
								? materialColors.surfaceDark[0]
								: materialColors.surfaceLight[0],
						paper:
							mode === "dark"
								? materialColors.surfaceDark[1]
								: materialColors.surfaceLight[1],
					},
					text: {
						primary:
							mode === "dark"
								? materialColors.surfaceDark.onSurface
								: materialColors.surfaceLight.onSurface,
						secondary:
							mode === "dark"
								? materialColors.surfaceDark.onSurfaceVariant
								: materialColors.surfaceLight.onSurfaceVariant,
					},
				},
				components: {
					MuiCssBaseline: {
						styleOverrides: {
							body: ({ theme }) => ({
								backgroundColor: theme.palette.background.default,
								color: theme.palette.text.primary,
								margin: 0,
								padding: 0,
								fontSize: '14px',
							}),
						},
					},
					MuiTypography: {
						defaultProps: {
							variant: "body1",
						},
						styleOverrides: {
							root: ({ theme }) => ({
								color: theme.palette.text.primary,
							}),
						},
					},
					MuiDivider: {
						styleOverrides: {
							root: ({ theme }) => ({
								paddingTop: theme.spacing(1),
								opacity: 1,
								borderColor: alpha(theme.palette.divider, 0.5), // or theme.palette.outline if defined
							}),
						},
					},
					MuiButton: {
						defaultProps: {
							variant: "contained",
						},
						styleOverrides: {
							root: ({ theme }) => ({

								borderRadius: 8,
								textTransform: "none",
								boxShadow: "none",
								"&:hover": { boxShadow: "none" },
								"&:active": { boxShadow: "none" },
								"&.Mui-focusVisible": { boxShadow: "none" },
							}),
						},
					},
					MuiPaper: {
						styleOverrides: {
							root: ({ theme }) => ({
								borderRadius: theme.shape.borderRadius,
							}),
						},
					},
					MuiIconButton: {
						defaultProps: {
							disableRipple: true, // cleaner hover effect
						},
						styleOverrides: {
							root: ({ theme }) => ({
								padding: 8,
								borderRadius: 2,
								color: theme.palette.text.secondary,

								'&:hover': {
									backgroundColor: theme.palette.action.hover,
									color: theme.palette.primary.main,
								},
								'&.Mui-disabled': {
									color: theme.palette.action.disabled,
								},
							}),
						},
					},

				},
				typography: {
					fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
					// Add specific typography styles based on Material Design 3
					h1: typographyTokens.headline.large,
					h2: typographyTokens.headline.medium,
					h3: typographyTokens.headline.small,
					h4: typographyTokens.title.large,
					h5: typographyTokens.title.medium,
					h6: typographyTokens.title.small,
					body1: typographyTokens.body.medium,
					body2: typographyTokens.body.small,
					button: typographyTokens.label.large,
					caption: typographyTokens.label.medium,
					overline: typographyTokens.label.small,
				},
				shape: {
					borderRadius: shapeTokens.corner.small,
				},
				shadows: [
					"none",
					mode === "dark"
						? elevationTokens.dark.elevation1
						: elevationTokens.light.elevation1,
					mode === "dark"
						? elevationTokens.dark.elevation2
						: elevationTokens.light.elevation2,
					mode === "dark"
						? elevationTokens.dark.elevation3
						: elevationTokens.light.elevation3,
					mode === "dark"
						? elevationTokens.dark.elevation4
						: elevationTokens.light.elevation4,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					// Additional shadow levels for MUI
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
					mode === "dark"
						? elevationTokens.dark.elevation5
						: elevationTokens.light.elevation5,
				],
			}),
		[mode]
	)

	// Convert Material colors to Mantine's expected format
	const mantineColorPalettes = useMemo(() => {
		// Mantine expects an array with exactly 10 color values (0-9)
		const convertToMantineFormat = (
			colorObj: Record<string | number, string>
		): [string, string, string, string, string, string, string, string, string, string] => {
			return [
				colorObj[10] || "#000000", // 0
				colorObj[20] || "#000000", // 1
				colorObj[30] || "#000000", // 2
				colorObj[40] || "#000000", // 3
				colorObj[50] || "#000000", // 4
				colorObj[60] || "#000000", // 5
				colorObj[70] || "#000000", // 6
				colorObj[80] || "#000000", // 7
				colorObj[90] || "#000000", // 8
				colorObj[95] || colorObj[99] || "#FFFFFF", // 9
			]
		}

		return {
			primaryPalette: convertToMantineFormat(materialColors.primary),
			secondaryPalette: convertToMantineFormat(materialColors.secondary),
			tertiaryPalette: convertToMantineFormat(materialColors.tertiary),
			errorPalette: convertToMantineFormat(materialColors.error),
			neutralPalette: convertToMantineFormat(materialColors.neutral),
			neutralVariantPalette: convertToMantineFormat(materialColors.neutralVariant),
		}
	}, [])

	// Mantine Theme with enhanced Material You support

	// Ant Design Theme Configuration
	const antdThemeConfig = useMemo((): AntdThemeConfig => {
		const antdTokens: AntdThemeConfig["token"] = {
			colorPrimary: muiTheme.palette.primary.main,
			colorInfo: muiTheme.palette.primary.main,
			colorSuccess: mode === "dark" ? "#66BB6A" : "#2E7D32", // Material success colors
			colorWarning: mode === "dark" ? "#FFB74D" : "#F57C00", // Material warning colors
			colorError: muiTheme.palette.error.main,
			colorTextBase:
				mode === "dark"
					? materialColors.surfaceDark.onSurface
					: materialColors.surfaceLight.onSurface,
			fontFamily: muiTheme.typography.fontFamily,
			borderRadius: muiTheme.shape.borderRadius,
			wireframe: false, // Making it more Material-like
		}
		// Component overrides for AntD to match Material You
		const antdComponents: AntdThemeConfig["components"] = {
			Button: {
				borderRadius: shapeTokens.corner.full,
				controlHeight: 36, // Material button height
				colorPrimary: muiTheme.palette.primary.main,
				algorithm: true,
			},
			Card: {
				borderRadius: shapeTokens.corner.medium,
			},
			Input: {
				borderRadius: shapeTokens.corner.small,
			},
			Select: {
				borderRadius: shapeTokens.corner.small,
			},
		}
		return {
			algorithm: mode === "dark" ? antdRawTheme.darkAlgorithm : antdRawTheme.defaultAlgorithm,
			token: antdTokens,
			components: antdComponents,
		}
	}, [
		mode,
		muiTheme.palette.primary.main,
		muiTheme.palette.error.main,
		muiTheme.typography.fontFamily,
		muiTheme.shape.borderRadius,
	])

	const toggleTheme = useCallback(() => {
		setMode((prevMode) => {
			return prevMode === "light" ? "dark" : "light"
		})
	}, [setMode])

	const contextValue = useMemo(
		() =>
			opt.Some({
				toggleTheme,
				theme: muiTheme,
				antdTheme: antdThemeConfig,
				mode,
				colors: materialColors,
				elevation: elevationTokens,
				shape: shapeTokens,
				typography: typographyTokens,
			}),
		[toggleTheme, muiTheme, antdThemeConfig, mode]
	)

	return (
		<themeCtx.Provider value={contextValue}>
			<MuiThemeProvider theme={muiTheme}>
				{/* Import Google Fonts with proper Material You weights */}
				<AntdConfigProvider theme={antdThemeConfig}>{children}</AntdConfigProvider>
			</MuiThemeProvider>
		</themeCtx.Provider>
	)
}

// UseTheme hook returns the complete theme context
export function UseTheme(): ThemeCtxType {
	return useContext(themeCtx).Expect("UseTheme must be used within a ThemeProvider")
}

export function UseAntDTheme() {
	return antdRawTheme.useToken()
}

// Enhanced Material Colors hook with semantic naming and proper Material Design 3 tokens
export function UseMaterialColors() {
	const { colors, mode, elevation, shape, typography } = UseTheme()
	const isDark = mode === "dark"

	const surfaces = isDark ? colors.surfaceDark : colors.surfaceLight

	// Return comprehensive Material You color and design tokens
	return {
		...colors,
		// Elevations
		elevation: isDark ? elevation.dark : elevation.light,
		// Shapes
		shape,
		// Typography
		typography,
		// Main color tokens with semantic naming
		surface: surfaces,
		onSurface: surfaces.onSurface,
		onSurfaceVariant: surfaces.onSurfaceVariant,
		// Primary colors
		primary: colors.primary[isDark ? 80 : 40],
		primaryContainer: isDark ? colors.primary[30] : colors.primary[90],
		onPrimary: isDark ? colors.primary[20] : colors.primary[100],
		onPrimaryContainer: isDark ? colors.primary[90] : colors.primary[10],
		// Secondary colors
		secondary: colors.secondary[isDark ? 80 : 40],
		secondaryContainer: isDark ? colors.secondary[30] : colors.secondary[90],
		onSecondary: isDark ? colors.secondary[20] : colors.secondary[100],
		onSecondaryContainer: isDark ? colors.secondary[90] : colors.secondary[10],
		// Tertiary colors
		tertiary: colors.tertiary[isDark ? 80 : 40],
		tertiaryContainer: isDark ? colors.tertiary[30] : colors.tertiary[90],
		onTertiary: isDark ? colors.tertiary[20] : colors.tertiary[100],
		onTertiaryContainer: isDark ? colors.tertiary[90] : colors.tertiary[10],
		// Error colors
		error: colors.error[isDark ? 80 : 40],
		errorContainer: isDark ? colors.error[30] : colors.error[90],
		onError: isDark ? colors.error[20] : colors.error[100],
		onErrorContainer: isDark ? colors.error[90] : colors.error[10],
		// Background containers
		background: surfaces[0],
		surfaceContainer: isDark ? surfaces.containerLow : surfaces.containerLow,
		surfaceContainerHigh: isDark ? surfaces.containerHigh : surfaces.containerHigh,
		surfaceContainerHighest: isDark ? surfaces[5] : surfaces[5],
		surfaceDim: surfaces.dim,
		surfaceBright: surfaces.bright,
		// Outline colors
		outline: isDark ? colors.neutralVariant[60] : colors.neutralVariant[50],
		outlineVariant: isDark ? colors.neutralVariant[30] : colors.neutralVariant[80],
		// Material state tokens
		stateLayer: {
			hover: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)",
			focus: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
			pressed: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
			dragged: isDark ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.16)",
		},
		// Convenience flags
		isDark,
	}
}
